import { useState } from 'react'
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2 } from 'lucide-react'
import { Button } from '../../components/Button/Button'

function Contact() {
	const [submitted, setSubmitted] = useState(false)

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		setSubmitted(true)
	}

	return (
		<main className='bg-brand-light min-h-screen'>
			{/* 1. SIMPLE HERO HEADER (MATCHED WITH HOME, ABOUT & PRODUCTS) */}
			<section className='bg-brand-dark text-white py-12 lg:py-16 border-b border-slate-800 border-t-2 border-t-brand-red'>
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					<h1 className='font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-4xl'>
						Get in Touch with{' '}
						<span className='text-brand-blue'>Africa Pipetek</span>
					</h1>
					<p className='text-slate-300 text-base sm:text-lg max-w-2xl mt-3 leading-relaxed'>
						Have questions about product availability, specifications, or
						deliveries? Reach out to our team directly or send us a message
						below.
					</p>
				</div>
			</section>

			{/* 2. MAIN CONTENT (INFO + FORM) */}
			<section className='py-12 lg:py-16'>
				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
					<div className='grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12'>
						{/* LEFT COLUMN: CONTACT DETAILS & HOURS */}
						<div className='lg:col-span-5 flex flex-col justify-between space-y-8'>
							<div>
								<h2 className='font-heading text-2xl font-bold text-brand-dark mb-6'>
									Direct Contact Information
								</h2>

								<div className='space-y-4'>
									{/* Location */}
									<div className='flex items-start gap-4 p-4 rounded-xl bg-white border border-brand-border shadow-sm'>
										<div className='p-3 rounded-lg bg-brand-blue/10 text-brand-blue shrink-0'>
											<MapPin className='w-5 h-5' />
										</div>
										<div>
											<h3 className='font-heading text-sm font-bold text-brand-dark'>
												Physical Address
											</h3>
											<p className='text-slate-600 text-sm mt-0.5'>
												8/10 Combrinck Street, Alrode, Alberton, 1451
											</p>
										</div>
									</div>

									{/* Phone */}
									<div className='flex items-start gap-4 p-4 rounded-xl bg-white border border-brand-border shadow-sm'>
										<div className='p-3 rounded-lg bg-brand-blue/10 text-brand-blue shrink-0'>
											<Phone className='w-5 h-5' />
										</div>
										<div>
											<h3 className='font-heading text-sm font-bold text-brand-dark'>
												Phone Lines
											</h3>
											<a
												href='tel:+27100061277'
												className='text-slate-600 hover:text-brand-blue text-sm block mt-0.5 font-medium transition-colors'>
												+27 (0) 10 006 1277
											</a>
										</div>
									</div>

									{/* Email */}
									<div className='flex items-start gap-4 p-4 rounded-xl bg-white border border-brand-border shadow-sm'>
										<div className='p-3 rounded-lg bg-brand-blue/10 text-brand-blue shrink-0'>
											<Mail className='w-5 h-5' />
										</div>
										<div>
											<h3 className='font-heading text-sm font-bold text-brand-dark'>
												Email Enquiries
											</h3>
											<a
												href='mailto:info@africapipetek.co.za'
												className='text-slate-600 hover:text-brand-blue text-sm block mt-0.5 font-medium transition-colors'>
												info@africapipetek.co.za
											</a>
										</div>
									</div>

									{/* Operating Hours */}
									<div className='flex items-start gap-4 p-4 rounded-xl bg-white border border-brand-border shadow-sm'>
										<div className='p-3 rounded-lg bg-brand-blue/10 text-brand-blue shrink-0'>
											<Clock className='w-5 h-5' />
										</div>
										<div>
											<h3 className='font-heading text-sm font-bold text-brand-dark'>
												Operating Hours
											</h3>
											<div className='text-slate-600 text-sm mt-0.5 space-y-0.5'>
												<p>Mon – Fri: 08:00 – 17:00</p>
												<p>Sat: 08:00 – 12:00</p>
											</div>
										</div>
									</div>
								</div>
							</div>

							{/* Quick Response Note */}
							<div className='p-5 rounded-2xl bg-brand-dark text-white border border-slate-800'>
								<p className='font-heading text-sm font-semibold text-brand-blue uppercase tracking-wider'>
									Direct Support
								</p>
								<p className='text-xs text-slate-300 mt-1'>
									All general inquiries and quote requests submitted through our
									form are reviewed promptly by our team.
								</p>
							</div>
						</div>

						{/* RIGHT COLUMN: CONTACT INQUIRY FORM */}
						<div className='lg:col-span-7'>
							<div className='bg-white p-6 sm:p-8 rounded-2xl border border-brand-border shadow-sm'>
								<h2 className='font-heading text-2xl font-bold text-brand-dark mb-2'>
									Send Us a Message
								</h2>
								<p className='text-slate-600 text-sm mb-6'>
									Fill out the details below and our team will get back to you.
								</p>

								{submitted ? (
									<div className='py-10 text-center bg-brand-blue/5 rounded-xl border border-brand-blue/20 p-6'>
										<CheckCircle2 className='w-10 h-10 text-brand-blue mx-auto mb-3' />
										<h3 className='font-heading text-lg font-bold text-brand-dark'>
											Message Received
										</h3>
										<p className='text-slate-600 text-sm mt-1 max-w-md mx-auto'>
											Thank you for contacting Africa Pipetek. We will respond
											to your query shortly.
										</p>
										<button
											onClick={() => setSubmitted(false)}
											className='mt-4 text-xs font-bold text-brand-blue hover:underline'>
											Send another message
										</button>
									</div>
								) : (
									<form onSubmit={handleSubmit} className='space-y-5'>
										<div className='grid grid-cols-1 sm:grid-cols-2 gap-5'>
											<div>
												<label className='block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5'>
													Full Name *
												</label>
												<input
													type='text'
													required
													placeholder='e.g. John Doe'
													className='w-full px-4 py-2.5 rounded-xl border border-brand-border focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue text-sm text-slate-900 transition-all'
												/>
											</div>

											<div>
												<label className='block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5'>
													Email Address *
												</label>
												<input
													type='email'
													required
													placeholder='e.g. john@company.com'
													className='w-full px-4 py-2.5 rounded-xl border border-brand-border focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue text-sm text-slate-900 transition-all'
												/>
											</div>
										</div>

										<div className='grid grid-cols-1 sm:grid-cols-2 gap-5'>
											<div>
												<label className='block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5'>
													Phone Number
												</label>
												<input
													type='tel'
													placeholder='e.g. +27 82 000 0000'
													className='w-full px-4 py-2.5 rounded-xl border border-brand-border focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue text-sm text-slate-900 transition-all'
												/>
											</div>

											<div>
												<label className='block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5'>
													Company / Organization
												</label>
												<input
													type='text'
													placeholder='e.g. Mining Ltd'
													className='w-full px-4 py-2.5 rounded-xl border border-brand-border focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue text-sm text-slate-900 transition-all'
												/>
											</div>
										</div>

										<div>
											<label className='block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5'>
												Product Line of Interest
											</label>
											<select className='w-full px-4 py-2.5 rounded-xl border border-brand-border focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue text-sm text-slate-900 transition-all bg-white'>
												<option value='general'>General Enquiry</option>
												<option value='hdpe'>HDPE Pressure Piping</option>
												<option value='pvc'>PVC Piping & Fittings</option>
											</select>
										</div>

										<div>
											<label className='block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5'>
												Your Message *
											</label>
											<textarea
												required
												rows={4}
												placeholder='Please detail your required pipe sizes, quantities, or specs...'
												className='w-full px-4 py-2.5 rounded-xl border border-brand-border focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue text-sm text-slate-900 transition-all'></textarea>
										</div>

										<Button
											type='submit'
											size='md'
											className='w-full flex items-center justify-center gap-2'>
											<Send className='w-4 h-4' />
											Submit Message
										</Button>
									</form>
								)}
							</div>
						</div>
					</div>
				</div>
			</section>
		</main>
	)
}

export default Contact
